import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLOR = {
  rule_created: "text-blue-700 bg-blue-50 border-blue-200",
  rule_run: "text-zinc-700 bg-zinc-100 border-zinc-200",
  approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
  auto_executed: "text-emerald-700 bg-emerald-50 border-emerald-200",
  rejected: "text-zinc-600 bg-zinc-100 border-zinc-200",
  auto_failed: "text-red-700 bg-red-50 border-red-200",
  approval_failed: "text-red-700 bg-red-50 border-red-200",
  account_mapping: "text-blue-700 bg-blue-50 border-blue-200",
};

export default function Activity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/activity");
      setItems(r.data.activity || []);
    } catch (e) { toast.error("Failed to load activity"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="activity-page" className="fade-in">
      <PageHeader
        eyebrow="Audit Trail"
        title="Activity Log"
        description="Chronological log of rule runs, approvals, rejections, and auto-executions."
        actions={
          <Button onClick={load} variant="outline" className="rounded-none" data-testid="activity-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
        <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="activity-list">
          {items.length === 0 && !loading && (
            <div className="text-center py-12 text-zinc-500 font-mono text-sm">No activity yet.</div>
          )}
          {items.map((a) => (
            <div key={a.id} className="px-5 py-4">
              <button
                data-testid={`activity-item-${a.id}`}
                onClick={() => setExpanded((e) => ({ ...e, [a.id]: !e[a.id] }))}
                className="w-full flex items-start gap-3 text-left"
              >
                <div className="mt-0.5">
                  {expanded[a.id] ? (
                    <ChevronDown className="w-4 h-4 text-zinc-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-mono border uppercase ${TYPE_COLOR[a.type] || "bg-zinc-50 text-zinc-700 border-zinc-200"}`}>
                      {a.type}
                    </span>
                    <span className="text-xs text-zinc-500 font-mono">
                      {new Date(a.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm mt-1 text-zinc-800">{a.message}</div>
                  {expanded[a.id] && a.details && Object.keys(a.details).length > 0 && (
                    <pre className="mt-2 bg-zinc-900 text-emerald-300 p-3 text-xs font-mono overflow-x-auto">
{JSON.stringify(a.details, null, 2)}
                    </pre>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
